"use client";

import classNames from "classnames";
import { FC, useMemo } from "react";

import { IButton, IconType } from "@types";
import { Icon } from "components/atoms/Icon/Icon";
import { TranslatedLink } from "components/atoms/TranslatedLink/TranslatedLink";

import { Locale } from "consts/locale";
import { useParams, usePathname } from "next/navigation";
import { addRefferalToContactLink } from "utils/referral-link";
import s from "./Button.module.scss";

export const Button: FC<IButton> = ({
  label,
  link = { href: "" },
  theme = "dark",
  icon,
  iconSize,
  preIcon = false,
  mirrorIcon = false,
  disabled = false,
  openLinkInNewTab = false,
  onClick,
}) => {
  const params = useParams();
  const locale = params?.locale as Locale;
  const pathname = usePathname();
  const iconColor = theme === "dark" ? "white" : "blue";
  const correctedIconSize = iconSize || (icon === IconType.CaretFilled ? "s" : "m");

  const content = useMemo(
    () => (
      <>
        {preIcon && icon && (
          <span className={s.ButtonPreIcon}>
            <Icon name={icon} size={correctedIconSize} color={iconColor} mirror={mirrorIcon} />
          </span>
        )}
        <span className={s.ButtonText}>{label}</span>
        {!preIcon && icon && (
          <span className={s.ButtonIcon}>
            <Icon name={icon} size={correctedIconSize} color={iconColor} mirror={mirrorIcon} />
          </span>
        )}
      </>
    ),
    [preIcon, icon, correctedIconSize, iconColor, mirrorIcon, label],
  );

  if (!link.href && typeof link === "string") {
    link = { href: link };
  }

  const hasLink = Boolean(link.href || link.slug);

  if (!hasLink || onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={classNames("button", s.Button, {
          [s.ButtonNone]: theme === "none",
          [s.ButtonLight]: theme === "light",
          [s.ButtonDark]: theme === "dark",
          [s.ButtonTextual]: theme === "textual",
          [s.ButtonTextualWhite]: theme === "textual-white",
        })}
      >
        {content}
      </button>
    );
  }

  const linkCopy = addRefferalToContactLink(link, locale, pathname);

  return (
    <TranslatedLink
      link={linkCopy}
      className={classNames("button", s.Button, {
        [s.ButtonNone]: theme === "none",
        [s.ButtonLight]: theme === "light",
        [s.ButtonDark]: theme === "dark",
        [s.ButtonTextual]: theme === "textual",
        [s.ButtonTextualWhite]: theme === "textual-white",
        [s.AnchorDisabled]: disabled,
      })}
      target={openLinkInNewTab ? "_blank" : "_self"}
      rel={openLinkInNewTab ? "noopener noreferrer external" : ""}
    >
      {content}
    </TranslatedLink>
  );
};
